import { useGroupFoldersData } from "@/data/use-group-folder-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { useEffect, useState } from "react";
import { ArtifactSourceDisplayName } from "../display/display-name/ArtifactSourceDisplayName";
import { FolderDisplayName } from "../display/display-name/artifacts";
import {
  ChipCombobox,
  ChipComboboxOption,
  ChipComboboxProps,
} from "../ui/chip-combobox";
import { Row } from "../ui/flex";

export function GroupFolderSelector({
  showEmpty = true,
  groupUuid,
  kitUuid,
  onSelectionChange,
}: {
  showEmpty?: boolean;
  groupUuid: string | null;
  kitUuid: string | null;
  onSelectionChange: (uuids: string[]) => void;
}) {
  const { folders: groupFolders } = useGroupFoldersData({
    groupUuid,
  });
  const { folders: kitFolders } = useKitFoldersData(groupUuid, kitUuid);
  const [groupOptions, setGroupOptions] = useState<ChipComboboxOption[]>([]);
  const [sharedOptions, setSharedOptions] = useState<ChipComboboxOption[]>([]);
  const [kitOptions, setKitOptions] = useState<ChipComboboxOption[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setKitOptions(
      kitFolders.map((folder) => ({
        value: folder.uuid,
        searchableText: folder.name,
        renderChip: () => <FolderDisplayName folder={folder} />,
        renderOption: () => <FolderDisplayName folder={folder} />,
      })),
    );
  }, [kitFolders]);

  useEffect(() => {
    const groupOptions: ChipComboboxOption[] = [];
    const sharedOptions: ChipComboboxOption[] = [];
    groupFolders.forEach((folder) => {
      if (folder.group?.uuid !== groupUuid) {
        sharedOptions.push({
          searchableText: folder.name,
          value: folder.uuid,
          renderChip: () => <FolderDisplayName folder={folder} />,
          renderOption: () => (
            <Row className="items-center justify-between gap-4">
              <FolderDisplayName folder={folder} />
              <ArtifactSourceDisplayName artifact={folder} />
            </Row>
          ),
        });
      } else {
        groupOptions.push({
          searchableText: folder.name,
          value: folder.uuid,
          renderChip: () => <FolderDisplayName folder={folder} />,
          renderOption: () => <FolderDisplayName folder={folder} />,
        });
      }
    });
    setGroupOptions(groupOptions);
    setSharedOptions(sharedOptions);
  }, [groupFolders]);

  // Include all group folders by default
  useEffect(() => {
    const uuids = kitFolders.map((folder) => folder.uuid);
    setSelected(uuids);
    onSelectionChange(uuids);
  }, [kitFolders, groupFolders]);

  const handleSelectionChange = (uuid: string, value: boolean) => {
    const updatedSelected = value
      ? [...selected, uuid]
      : selected.filter((v) => v !== uuid);
    setSelected(updatedSelected);
    onSelectionChange(updatedSelected);
  };

  const groups: ChipComboboxProps["groups"] = [];

  if (kitOptions.length > 0) {
    groups.push({
      groupName: "Preset Folders",
      options: kitOptions,
    });
  }

  if (groupOptions.length > 0) {
    groups.push({
      groupName: "Workspace Folders",
      options: groupOptions,
    });
  }

  if (sharedOptions.length > 0) {
    groups.push({
      groupName: "Shared Folders",
      options: sharedOptions,
    });
  }

  if (groups.length === 0 && !showEmpty) {
    return null;
  }

  return (
    <ChipCombobox
      groups={groups}
      selected={selected}
      onSelectionChange={handleSelectionChange}
    />
  );
}
